import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function maskCard(v) { return v.replace(/\D/g,'').slice(0,16).replace(/(.{4})/g,'$1 ').trim(); }
function maskDate(v) { return v.replace(/\D/g,'').slice(0,4).replace(/^(\d{2})(\d)/,'$1/$2'); }
function maskCPF(v)  { return v.replace(/\D/g,'').slice(0,11).replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/,'$1.$2.$3-$4').replace(/-$/,''); }
function maskCEP(v)  { return v.replace(/\D/g,'').slice(0,8).replace(/^(\d{5})(\d)/,'$1-$2'); }
function maskPhone(v) {
  const d = v.replace(/\D/g,'').slice(0,11);
  return d.length > 10
    ? d.replace(/(\d{2})(\d{5})(\d{4})/,'($1) $2-$3')
    : d.replace(/(\d{2})(\d{4})(\d{4})/,'($1) $2-$3');
}

export default function Checkout() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { usuario, carregarPerfil } = useAuth();
  const [planos, setPlanos] = useState([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [sucesso, setSucesso] = useState(null);

  const [form, setForm] = useState({
    cpf: '', cep: '', telefone: '', nome_cartao: '', numero_cartao: '', validade: '', cvv: '',
  });

  const planoSlug = params.get('plano') || 'basico';
  const plano = useMemo(() => planos.find(p => p.slug === planoSlug), [planos, planoSlug]);

  useEffect(() => {
    api.get('/assinaturas/planos')
      .then(({ data }) => setPlanos(data))
      .catch(() => setErro('Não foi possível carregar os planos.'));
  }, []);

  const upd = (field) => (e) => {
    let v = e.target.value;
    if (field === 'numero_cartao') v = maskCard(v);
    if (field === 'validade')      v = maskDate(v);
    if (field === 'cpf')           v = maskCPF(v);
    if (field === 'cep')           v = maskCEP(v);
    if (field === 'telefone')      v = maskPhone(v);
    setForm(f => ({ ...f, [field]: v }));
  };

  const assinar = async e => {
    e.preventDefault();
    if (!plano) return;
    setCarregando(true); setErro('');
    try {
      const res = await api.post('/assinaturas/assinar', {
        plano_id: plano.id,
        cpf: form.cpf,
        cep: form.cep,
        telefone: form.telefone,
        nome_cartao: form.nome_cartao,
        numero_cartao: form.numero_cartao.replace(/\s/g, ''),
        validade: form.validade,
        cvv: form.cvv,
        nome: usuario?.nome,
        email: usuario?.email,
      });
      await carregarPerfil();
      setSucesso(res.data);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao criar assinatura. Verifique os dados e tente novamente.');
    } finally { setCarregando(false); }
  };

  const S = {
    page: { background: 'var(--cream)', minHeight: 'calc(100vh - 112px)', padding: '56px 0' },
    wrap: { maxWidth: 680 },
    grid: { marginTop: 36 },
    card: { padding: '32px 36px' },
    trialBadge: {
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'linear-gradient(135deg,rgba(216,189,105,.18),rgba(154,86,55,.08))',
      border: '1px solid rgba(216,189,105,.35)', borderRadius: 'var(--r-md)',
      padding: '18px 22px', marginBottom: 24,
    },
    trialIcon: { fontSize: 28, flexShrink: 0 },
    trialTitle: { fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 600, marginBottom: 4 },
    trialText: { fontSize: 13, color: 'var(--ink-muted)', lineHeight: 1.55 },
    summary: {
      background: '#fff', border: '1px solid var(--border)',
      borderRadius: 'var(--r-md)', padding: '24px',
      boxShadow: 'var(--shadow-sm)', position: 'sticky', top: 132,
    },
    planName: { fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--rust)', fontWeight: 900, marginBottom: 8 },
    planPrice: { fontFamily: "'Playfair Display',serif", fontSize: 38, fontWeight: 600, lineHeight: 1, marginBottom: 4 },
    divider: { borderTop: '1px solid var(--border)', margin: '16px 0' },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginBottom: 8 },
    highlight: { color: 'var(--rust)', fontWeight: 900 },
    note: { fontSize: 11, color: 'var(--ink-muted)', lineHeight: 1.55, marginTop: 12 },
  };

  if (sucesso) return (
    <>
      <Navbar />
      <div style={S.page}>
        <div className="container" style={{ ...S.wrap, textAlign: 'center' }}>
          <div style={{ fontSize: 60, marginBottom: 24 }}>🎉</div>
          <div className="section-label">Tudo certo</div>
          <h1 className="section-title">Período de teste ativado!</h1>
          <div className="divider-gold" style={{ margin: '18px auto' }} />
          <p style={{ color: 'var(--ink-muted)', fontSize: 16, lineHeight: 1.7, marginBottom: 10 }}>
            Você tem <strong style={{ color: 'var(--rust)' }}>7 dias gratuitos</strong> para explorar tudo que a plataforma Heritage oferece.
          </p>
          <p style={{ color: 'var(--ink-muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 32 }}>
            A primeira cobrança de <strong>R$ {Number(sucesso.plano?.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> será realizada em{' '}
            <strong>{sucesso.primeira_cobranca ? new Date(sucesso.primeira_cobranca).toLocaleDateString('pt-BR') : '7 dias'}</strong>.
            Você pode cancelar a qualquer momento antes disso sem custo.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Link to="/veiculos" className="btn btn-primary">Explorar veículos</Link>
            <Link to="/minha-assinatura" className="btn btn-outline">Minha assinatura</Link>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Navbar />
      <div style={S.page}>
        <div className="container" style={S.wrap}>
          <div className="page-header">
            <div className="eyebrow">Assinatura</div>
            <h1 className="section-title">Iniciar período de teste</h1>
            <div className="gold-rule" />
            <p style={{ color: 'var(--ink-muted)', fontSize: 14, lineHeight: 1.6 }}>
              Cadastre seu cartão para garantir o acesso. Nenhuma cobrança será feita agora — o valor só é debitado após os 7 dias gratuitos.
            </p>
          </div>

          {plano ? (
            <div className="checkout-grid" style={S.grid}>
              {/* Formulário */}
              <div className="card" style={S.card}>
                <div style={S.trialBadge}>
                  <div style={S.trialIcon}>🔒</div>
                  <div>
                    <div style={S.trialTitle}>7 dias gratuitos, sem cobrança agora</div>
                    <div style={S.trialText}>
                      Seu cartão será cadastrado mas <strong>não haverá débito hoje</strong>. A primeira cobrança de R$ {Number(plano.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ocorre apenas no 8º dia.
                    </div>
                  </div>
                </div>

                {erro && <div className="alert alert-danger">{erro}</div>}

                <form onSubmit={assinar}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>CPF do titular</label>
                      <input value={form.cpf} onChange={upd('cpf')} placeholder="000.000.000-00" required />
                    </div>
                    <div className="form-group">
                      <label>CEP</label>
                      <input value={form.cep} onChange={upd('cep')} placeholder="00000-000" maxLength={9} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Telefone do titular do cartão</label>
                    <input value={form.telefone} onChange={upd('telefone')} placeholder="(00) 00000-0000" maxLength={15} required />
                  </div>
                  <div className="form-group">
                    <label>Nome no cartão</label>
                    <input value={form.nome_cartao} onChange={upd('nome_cartao')} placeholder="Como está escrito no cartão" required />
                  </div>
                  <div className="form-group">
                    <label>Número do cartão</label>
                    <input value={form.numero_cartao} onChange={upd('numero_cartao')} placeholder="0000 0000 0000 0000" maxLength={19} required />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Validade</label>
                      <input value={form.validade} onChange={upd('validade')} placeholder="MM/AA" maxLength={5} required />
                    </div>
                    <div className="form-group">
                      <label>CVV</label>
                      <input value={form.cvv} onChange={e => setForm(f => ({ ...f, cvv: e.target.value.replace(/\D/g,'').slice(0,4) }))} placeholder="000" maxLength={4} required />
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={carregando}>
                    {carregando ? 'Processando…' : `Iniciar 7 dias grátis — ${plano.nome}`}
                  </button>
                  <p style={{ fontSize: 11, color: 'var(--ink-muted)', textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
                    Cancele quando quiser. Sem cobrança nos primeiros 7 dias.
                  </p>
                </form>
              </div>

              {/* Resumo lateral */}
              <div style={S.summary}>
                <div style={S.planName}>{plano.nome}</div>
                <div style={S.planPrice}>
                  R$ {Number(plano.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  <span style={{ fontSize: 14, color: 'var(--ink-muted)', fontFamily: 'Lato,sans-serif', fontWeight: 400 }}>/mês</span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: 0 }}>{plano.descricao}</p>

                <div style={S.divider} />

                <div style={S.row}>
                  <span>Hoje</span>
                  <span style={S.highlight}>R$ 0,00</span>
                </div>
                <div style={S.row}>
                  <span>A partir do 8º dia</span>
                  <span style={{ fontWeight: 700 }}>R$ {Number(plano.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</span>
                </div>

                <div style={S.divider} />

                <ul style={{ listStyle: 'none', display: 'grid', gap: 8 }}>
                  {['Acesso imediato à plataforma', 'Cancele antes do 8º dia sem custo', 'Cobrança mensal recorrente após trial'].map(t => (
                    <li key={t} style={{ fontSize: 12, color: 'var(--ink-muted)', paddingLeft: 16, position: 'relative', lineHeight: 1.4 }}>
                      <span style={{ position: 'absolute', left: 0, color: 'var(--rust)', fontWeight: 900 }}>✓</span>{t}
                    </li>
                  ))}
                </ul>
                <p style={S.note}>Ao continuar, você concorda com os <Link to="/termos" style={{ color: 'var(--rust)' }}>Termos de uso</Link>.</p>
              </div>
            </div>
          ) : (
            <p style={{ color: 'var(--ink-muted)' }}>Carregando plano…</p>
          )}
        </div>
      </div>
    </>
  );
}
